/**
 * US state reference: IANA time zone, whether the state observes DST, and any
 * calling-hour rule that is TIGHTER than the federal 8am-9pm window.
 *
 * These values are the agency's *configured* starting point, not legal advice.
 * Compliance counsel must review them before the platform dials a real number.
 */
export interface StateRef {
  code: string;
  name: string;
  /** IANA zone used for the called party's local time. */
  tz: string;
  /** Local hour the state permits calling from (24h, may be fractional). */
  open: number;
  /** Local hour the state requires calling to stop (24h, may be fractional). */
  close: number;
  /** Human description of the state rule, when it is tighter than federal. */
  rule: string | null;
  /**
   * Whether an ARTIFICIAL voice may lawfully solicit in this state at all.
   *
   * Separate from consent, and it outranks it. Federal law says an artificial
   * voice needs prior express written consent; several states say something
   * different and stricter, and a state that prohibits solicitation by recorded
   * message does not care how good the consent is.
   *
   * Michigan is the worked example. MCL 445.111a: "a person shall not make a
   * telephone solicitation that consists in whole or in part of a recorded
   * message." The only exemptions in MCL 445.111e are charitable solicitation,
   * public safety solicitation, and section 527 political organisations — no
   * insurance carve-out, and no consent carve-out. The insurance-agent
   * exclusion elsewhere in the Act attaches to "home solicitation sale", a
   * different defined term in a different section.
   *
   * DEFAULT IS `unclear`, AND `unclear` REFUSES.
   *
   * Forty-nine of these rows have not been researched, and this file will not
   * mark a state permitted on the grounds that nobody has looked. A permissive
   * default here is the one that puts an artificial voice on a phone in a state
   * that bans it, at $500 to $1,500 a call, and the agency finds out from a
   * lawsuit. A state moves to `permitted` when counsel says so in writing and
   * not before — which also makes this field the map of where the AI dialer can
   * actually be switched on.
   */
  artificialVoice: 'permitted' | 'prohibited' | 'unclear';
  /**
   * What was actually found out about this state, and what is still missing.
   *
   * `unclear` covers two very different situations and the difference is worth
   * real money: nobody has looked, versus somebody looked, it appears fine, and
   * it is waiting on a written opinion. Both refuse — but only one of them is a
   * short conversation away from being a market.
   *
   * So the status stays binary and honest, and this carries the detail: the
   * statute, the exemption, the case, and the precise question left for counsel.
   * A state with a note is a state somebody can act on.
   */
  voiceNote: string | null;
}

const S = (
  code: string, name: string, tz: string, open: number, close: number, rule: string | null,
  artificialVoice: 'permitted' | 'prohibited' | 'unclear' = 'unclear',
  voiceNote: string | null = null,
): StateRef => ({ code, name, tz, open, close, rule, artificialVoice, voiceNote });

export const STATES: readonly StateRef[] = [
  S('AL', 'Alabama', 'America/Chicago', 8, 20, '8am–8pm'),
  S('AK', 'Alaska', 'America/Anchorage', 8, 21, null),
  S('AZ', 'Arizona', 'America/Phoenix', 8, 21, null, 'unclear',
    'Researched, looks the most open of the three, awaiting sign-off. A.R.S. 44-1271 et seq. '
    + 'exempts "a person acting within the scope of a license issued under title 20" — Arizona '
    + 'insurance licensing — so no registration and no $100,000 bond. Crucially, Arizona bars an '
    + 'artificial or prerecorded voice to a residential line "unless ... made with the PRIOR '
    + 'EXPRESS CONSENT of the called party" — a consent exception, which is exactly what Michigan '
    + 'lacks. Arizona enforces the federal registry and keeps no state list. Confirm with counsel '
    + 'before switching this to permitted.'),
  S('AR', 'Arkansas', 'America/Chicago', 8, 20.5, '8am–8:30pm'),
  S('CA', 'California', 'America/Los_Angeles', 8, 21, null),
  S('CO', 'Colorado', 'America/Denver', 8, 20, '8am–8pm'),
  S('CT', 'Connecticut', 'America/New_York', 9, 21, '9am–9pm'),
  S('DC', 'District of Columbia', 'America/New_York', 8, 21, null),
  S('DE', 'Delaware', 'America/New_York', 8, 21, null),
  S('FL', 'Florida', 'America/New_York', 8, 20, '8am–8pm'),
  S('GA', 'Georgia', 'America/New_York', 8, 21, null),
  S('HI', 'Hawaii', 'Pacific/Honolulu', 8, 21, null),
  S('IA', 'Iowa', 'America/Chicago', 8, 21, null),
  S('ID', 'Idaho', 'America/Boise', 8, 21, null),
  S('IL', 'Illinois', 'America/Chicago', 8, 21, null),
  S('IN', 'Indiana', 'America/Indiana/Indianapolis', 8, 21, null),
  S('KS', 'Kansas', 'America/Chicago', 8, 21, null),
  S('KY', 'Kentucky', 'America/New_York', 8, 21, null),
  S('LA', 'Louisiana', 'America/Chicago', 8, 20, '8am–8pm'),
  S('MA', 'Massachusetts', 'America/New_York', 8, 20, '8am–8pm'),
  S('MD', 'Maryland', 'America/New_York', 8, 21, null),
  S('ME', 'Maine', 'America/New_York', 8, 21, null),
  /*
   * MICHIGAN OPENS AT NINE, NOT EIGHT.
   *
   * MCL 750.540e(f) names "making an unsolicited commercial telephone call that
   * is received between the hours of 9 p.m. and 9 a.m." — an hour later in the
   * morning than the federal floor the rest of this table defaults to.
   *
   * It is not a clean prohibition. The offence sits under a chapeau requiring
   * the caller to act maliciously, with intent to harass or "to disturb the
   * peace and quiet of another person", and whether an ordinary 8:30am
   * insurance call clears that bar is a question for counsel — not one this
   * table should be quietly answering in the permissive direction.
   *
   * Which is why it is set to 9. This file exists to take the stricter of the
   * two, and the trade is lopsided: the loose setting buys one hour of morning
   * dial time in one state and carries a misdemeanor argument; the tight
   * setting costs that hour and carries none. Michigan is also where every call
   * in the pilot will be placed, so of the fifty rows here it is the one where
   * a default nobody had checked was actually going to be dialled.
   */
  S('MI', 'Michigan', 'America/Detroit', 9, 21, '9am–9pm (MCL 750.540e)', 'prohibited',
    'MCL 445.111a bans a telephone solicitation consisting in whole or in part of a recorded '
    + 'message. The only exemptions (MCL 445.111e) are charitable, public safety and section 527 '
    + 'organisations — no insurance carve-out and NO CONSENT EXCEPTION. The insurance-agent '
    + 'exclusion elsewhere in the Act attaches to "home solicitation sale", a different term in a '
    + 'different section. Open question for counsel: does "recorded message" reach a real-time '
    + 'synthesised voice, which is not a recording?'),
  S('MN', 'Minnesota', 'America/Chicago', 8, 21, null),
  S('MO', 'Missouri', 'America/Chicago', 8, 21, null),
  S('MS', 'Mississippi', 'America/Chicago', 8, 21, null),
  S('MT', 'Montana', 'America/Denver', 8, 21, null),
  S('NC', 'North Carolina', 'America/New_York', 8, 21, null),
  S('ND', 'North Dakota', 'America/Chicago', 8, 21, null),
  S('NE', 'Nebraska', 'America/Chicago', 8, 21, null),
  S('NH', 'New Hampshire', 'America/New_York', 8, 20, '8am–8pm'),
  S('NJ', 'New Jersey', 'America/New_York', 8, 21, null),
  S('NM', 'New Mexico', 'America/Denver', 8, 21, null),
  S('NV', 'Nevada', 'America/Los_Angeles', 8, 21, null),
  S('NY', 'New York', 'America/New_York', 8, 21, null),
  S('OH', 'Ohio', 'America/New_York', 8, 21, null),
  S('OK', 'Oklahoma', 'America/Chicago', 8, 20, '8am–8pm'),
  S('OR', 'Oregon', 'America/Los_Angeles', 8, 21, null),
  S('PA', 'Pennsylvania', 'America/New_York', 8, 21, null),
  // Atlantic Standard Time, and the one US jurisdiction here that never moves:
  // Puerto Rico does not observe daylight saving. For half the year it is an
  // hour ahead of New York and for the other half it is level with it, which a
  // floor working from "Eastern plus or minus" will get wrong twice a year.
  // The IANA zone handles it; nobody should be doing this arithmetic by hand.
  S('PR', 'Puerto Rico', 'America/Puerto_Rico', 8, 21, null),
  S('RI', 'Rhode Island', 'America/New_York', 8, 21, null),
  S('SC', 'South Carolina', 'America/New_York', 8, 21, null),
  S('SD', 'South Dakota', 'America/Chicago', 8, 21, null),
  S('TN', 'Tennessee', 'America/Chicago', 8, 21, null),
  S('TX', 'Texas', 'America/Chicago', 8, 21, null, 'unclear',
    'Researched, looks open, awaiting sign-off. Tex. Bus. & Com. Code 302.053(3) exempts "a person '
    + 'who holds a license issued under the Insurance Code if the solicited transaction is governed '
    + 'by that code" — so no registration and no $10,000 security deposit. Courts enforce it: '
    + 'Sutton v. Senior Life Insurance Co. dismissed a 302.101 claim because the defendant held a '
    + 'Texas life licence and the call concerned life insurance. No independent Texas ban on '
    + 'artificial voice found, so the federal written-consent standard governs — but Texas gives a '
    + 'private right of action, which makes a federal mistake cost more here than elsewhere. '
    + 'Confirm with counsel before switching this to permitted.'),
  S('UT', 'Utah', 'America/Denver', 8, 21, null),
  S('VA', 'Virginia', 'America/New_York', 8, 21, null),
  S('VT', 'Vermont', 'America/New_York', 8, 21, null),
  S('WA', 'Washington', 'America/Los_Angeles', 8, 20, '8am–8pm'),
  S('WI', 'Wisconsin', 'America/Chicago', 8, 21, null),
  S('WV', 'West Virginia', 'America/New_York', 8, 21, null),
  S('WY', 'Wyoming', 'America/Denver', 8, 21, null),
];

export const STATE_BY_CODE: ReadonlyMap<string, StateRef> = new Map(
  STATES.map((s) => [s.code, s]),
);
const BY_NAME: ReadonlyMap<string, StateRef> = new Map(
  STATES.map((s) => [s.name.toLowerCase(), s]),
);

/** Resolve "fl", "Florida", " FLA " → the Florida record. Null when unknown. */
export function resolveState(input: string | null | undefined): StateRef | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  const byCode = STATE_BY_CODE.get(upper);
  if (byCode) return byCode;
  const byName = BY_NAME.get(raw.toLowerCase());
  if (byName) return byName;
  // Common informal abbreviations that are not the postal code.
  const informal: Record<string, string> = {
    FLA: 'FL', CALI: 'CA', CALIF: 'CA', PENN: 'PA', MASS: 'MA',
    TENN: 'TN', WASH: 'WA', ARIZ: 'AZ', COLO: 'CO', CONN: 'CT',
    ILL: 'IL', IND: 'IN', KANS: 'KS', MICH: 'MI', MINN: 'MN',
    MISS: 'MS', NEB: 'NE', NEV: 'NV', OKLA: 'OK', OREG: 'OR',
    WISC: 'WI', WIS: 'WI',
  };
  const mapped = informal[upper.replace(/[^A-Z]/g, '')];
  return mapped ? STATE_BY_CODE.get(mapped) ?? null : null;
}

/**
 * First three ZIP digits → state, for rows that carry a ZIP but no state.
 *
 * WHY THIS TABLE IS WORTH CHECKING DIGIT BY DIGIT
 * A lead imported without a state gets one from here, and that state decides
 * two things that matter: the hours it is legal to call them, and which
 * producers are licensed to work them. A wrong row is not a cosmetic error. It
 * is a Massachusetts family dialled on New York's clock by an agent who cannot
 * legally sell to them, and nothing anywhere reports it as a problem.
 *
 * The first block was wrong in six places. 010–027 is Massachusetts, not New
 * York. 028–029 is Rhode Island. 030–038 is New Hampshire, 039–049 Maine, and
 * 050–059 is Vermont end to end. 006–009 was filed under Connecticut, which is
 * 060–069 — one digit along, and a different jurisdiction entirely: those are
 * Puerto Rico and the US Virgin Islands.
 *
 * Ranges with no single state are deliberately absent rather than guessed:
 * 008 (US Virgin Islands), 090–098 (Armed Forces Europe), 962–966 (Armed
 * Forces Pacific) and 969 (Guam and the Northern Marianas) resolve to null, so
 * the lead reaches the compliance gate with no state and is held. That is the
 * safe answer. A guess here would be silently wrong forever.
 */
const ZIP_PREFIX: ReadonlyArray<readonly [number, number, string]> = [
  [5, 5, 'NY'], [6, 7, 'PR'], [9, 9, 'PR'],
  [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'],
  [50, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'],
  [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'],
  [200, 205, 'DC'], [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'],
  [270, 289, 'NC'], [290, 299, 'SC'], [300, 319, 'GA'], [320, 349, 'FL'],
  [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'], [398, 399, 'GA'],
  [400, 427, 'KY'], [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'],
  [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'],
  [580, 588, 'ND'], [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'],
  [660, 679, 'KS'], [680, 693, 'NE'], [700, 714, 'LA'], [716, 729, 'AR'],
  [730, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'], [820, 831, 'WY'],
  [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'],
  [889, 898, 'NV'], [900, 961, 'CA'], [967, 968, 'HI'], [970, 979, 'OR'],
  [980, 994, 'WA'], [995, 999, 'AK'],
];

export function stateFromZip(zip: string | null | undefined): StateRef | null {
  if (!zip) return null;
  const digits = String(zip).replace(/\D/g, '').slice(0, 5);
  if (digits.length < 3) return null;
  const p = Number(digits.slice(0, 3));
  for (const [lo, hi, code] of ZIP_PREFIX) {
    if (p >= lo && p <= hi) return STATE_BY_CODE.get(code) ?? null;
  }
  return null;
}

/**
 * The called party's local wall-clock hour, as a float (13.5 === 1:30pm).
 * Uses the IANA zone, so DST is handled by the platform rather than by us.
 */
export function localHour(stateCode: string, at: Date = new Date()): number {
  const st = STATE_BY_CODE.get(stateCode);
  const tz = st?.tz ?? 'America/New_York';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(at);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return (hour % 24) + minute / 60;
}

export function localDayOfWeek(stateCode: string, at: Date = new Date()): number {
  const st = STATE_BY_CODE.get(stateCode);
  const tz = st?.tz ?? 'America/New_York';
  const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(at);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
}
